import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { EndowmentEffect } from "../../../../translations/experiments/EndowmentEffect";
import { colors, commonStyles, chartColors } from "../../../../components/colors";

const buyerColor = chartColors.primary[0];    // e.g. blue/green
const sellerColor = chartColors.primary[1];   // e.g. blue

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 680,
    maxWidth: 990,
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 380,
    boxSizing: "border-box"
};

const minEuro = 0;
const maxEuro = 80;

type ParticipantDistribution = {
    euro: number;
    ["buyer"]: number;
    ["seller"]: number;
};

export const ResultScreenGlobalComponent = () => {
    const { getGroupData, userData } = useSession();
    const { language } = useLanguage();
    const [dataOwnGroup, setDataOwnGroup] = useState<ParticipantDistribution[]>([]);
    const [globalDist, setGlobalDist] = useState<ParticipantDistribution[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingGlobal, setLoadingGlobal] = useState(true);

    // Use experiment-local translations directly
    const { resultsGlobal } = EndowmentEffect[language].screens;
    const loadingText   = resultsGlobal.loading;
    const yourClassTitle= resultsGlobal.yourClass;
    const allClassesTitle=resultsGlobal.allClasses;
    const buyerLabel    = resultsGlobal.buyer;
    const sellerLabel   = resultsGlobal.seller;
    const yAxisLabel    = resultsGlobal.yAxisLabel;
    const euroSymbol    = resultsGlobal.euro;
    const title         = resultsGlobal.title;

    const eigeneGruppe = userData?.gruppe ?? "";
    const normalizeGroup = (name: string | undefined | null) =>
        (name ?? "").trim().toLowerCase();

    const toBarChartData = (agg: { buyer: Record<string, number>, seller: Record<string, number> }) => {
        const bars: ParticipantDistribution[] = [];
        for (let i = minEuro; i <= maxEuro; i++) {
            bars.push({
                euro: i,
                buyer: agg?.buyer?.[i]?.valueOf() || 0,
                seller: agg?.seller?.[i]?.valueOf() || 0
            });
        }
        return bars;
    };

    useEffect(() => {
        setLoading(true);
        getGroupData().then(allData => {
            const agg = {
                buyer: {} as Record<string, number>,
                seller: {} as Record<string, number>
            };
            for (let i = minEuro; i <= maxEuro; i++) agg.buyer[i] = 0;
            for (let i = 1; i <= maxEuro; i++) agg.seller[i] = 0;

            const groupNorm = normalizeGroup(eigeneGruppe);
            allData?.forEach((dat: any) => {
                const datNorm = normalizeGroup(dat.gruppe);
                if (datNorm !== groupNorm) return;

                const endo = dat?.EndowmentEffect?.["0"];
                if (endo?.role === "buyer" && typeof endo.willingToPay === "number" && endo.willingToPay >= minEuro && endo.willingToPay <= maxEuro) {
                    agg.buyer[endo.willingToPay]++;
                }
                if (endo?.role === "seller" && typeof endo.askedPrice === "number" && endo.askedPrice >= 1 && endo.askedPrice <= maxEuro) {
                    agg.seller[endo.askedPrice]++;
                }
            });
            setDataOwnGroup(toBarChartData(agg));
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [getGroupData, eigeneGruppe]);

    useEffect(() => {
        setLoadingGlobal(true);
        fetch("/api/aggregateGlobalData")
            .then(res => res.json())
            .then(apiJson => {
                const agg = apiJson?.data?.EndowmentEffect;
                setGlobalDist(toBarChartData(agg ?? { buyer: {}, seller: {} }));
                setLoadingGlobal(false);
            }).catch(() => setLoadingGlobal(false));
    }, []);

    const barSize = 14;
    const barCategoryGap = "25%";

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const euro = payload[0]?.payload?.euro;
            return (
                <div style={{
                    background: "#fff",
                    border: `1px solid ${colors.silver}`,
                    padding: 10,
                    borderRadius: 8,
                    fontSize: 14,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        {euro} {euroSymbol}
                    </div>
                    {payload.map((p: any) => (
                        <div key={p.dataKey} style={{ color: p.color || colors.textMedium }}>
                            {p.name}: {p.value}
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {title}
            </h2>
            <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 48,
                marginTop: 24,
                width: "100%"
            }}>
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10
                    }}>
                        {yourClassTitle}
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart
                                data={dataOwnGroup}
                                margin={{ top: 28, right: 16, left: 10, bottom: 38 }}
                                barCategoryGap={barCategoryGap}
                            >
                                <XAxis
                                    dataKey="euro"
                                    type="number"
                                    domain={[minEuro, maxEuro]}
                                    tick={{ fontSize: 13 }}
                                    label={{
                                        value: euroSymbol,
                                        position: "insideBottomRight",
                                        offset: 10,
                                        fontSize: 18,
                                        fontWeight: 700,
                                        fill: colors.textMedium
                                    }}
                                    interval={4}
                                />
                                <YAxis allowDecimals={false} label={{ value: yAxisLabel, angle: -90, position: "insideLeft", offset: 10, fontSize: 14 }}/>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar dataKey="buyer" fill={buyerColor} name={buyerLabel} barSize={barSize} />
                                <Bar dataKey="seller" fill={sellerColor} name={sellerLabel} barSize={barSize} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10
                    }}>
                        {allClassesTitle}
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                        {loadingGlobal ? (
                            <div style={{ color: colors.brillantBlue, fontSize: 17, textAlign: "center", marginTop: 40 }}>
                                {loadingText}
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart
                                    data={globalDist}
                                    margin={{ top: 28, right: 16, left: 10, bottom: 38 }}
                                    barCategoryGap={barCategoryGap}
                                >
                                    <XAxis
                                        dataKey="euro"
                                        type="number"
                                        domain={[minEuro, maxEuro]}
                                        tick={{ fontSize: 13 }}
                                        label={{
                                            value: euroSymbol,
                                            position: "insideBottomRight",
                                            offset: 10,
                                            fontSize: 18,
                                            fontWeight: 700,
                                            fill: colors.textMedium
                                        }}
                                        interval={4}
                                    />
                                    <YAxis allowDecimals={false} label={{ value: yAxisLabel, angle: -90, position: "insideLeft", offset: 10, fontSize: 14 }}/>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey="buyer" fill={buyerColor} name={buyerLabel} barSize={barSize} />
                                    <Bar dataKey="seller" fill={sellerColor} name={sellerLabel} barSize={barSize} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ResultScreenGlobal: ScreenConfig = {
    component: ResultScreenGlobalComponent,
    defaultResult: {},
    canContinueDefault: true,
};